import { LinkIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import { CanvasSimpleSelect } from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  EmbedShape,
  embedType,
  EmbedOperator,
  embedOperators,
  EqualityOperator,
  equalityOperators,
  EmbedProps,
  equalityOperatorOptions,
  metadataType,
  Dimensions,
  embedOperatorOptions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";

export function isEmbedShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is EmbedShape {
  return shape.type === embedType;
}

export class EmbedTool extends ToolStateNode {
  static override id = embedType;

  static override uiToolDef = (editor: Editor) => ({
    id: embedType,
    icon: LinkIcon,
    label: EmbedShapeUtil.docs.title,
    kbd: "w",
    defaultColor: EmbedShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(embedType),
    docs: () => (
      <NodeDocs editor={editor} component={Embed} util={EmbedShapeUtil} />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: embedType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class EmbedShapeUtil extends BindableUtil<EmbedShape> {
  static override migrations = createMigrations(embedType);
  static override type = embedType;

  override getType() {
    return embedType;
  }

  static override docs = {
    defaultColor: "teal" as const,
    title: "Embed",
    description: "Select/Reject based on what type of embed is in the post.",
  };

  static override props: RecordProps<EmbedShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    operator: T.literalEnum(...equalityOperators),
    embed_name: T.literalEnum(...embedOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 300,
      h: MIN_H,
      operator: equalityOperators[0],
      embed_name: embedOperators[0],
      metadata: undefined,
    };
  }

  override component(shape: EmbedShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <Embed
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function Embed({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: EmbedShape;
  className?: string;
  updateShape: (shape: EmbedShape, props: Partial<EmbedProps>) => void;
}) {
  const variant = shape.props.operator === "==" ? "green" : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={EmbedShapeUtil.docs} />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Comparison Operator"
            className="w-[120px]"
            value={shape.props.operator}
            options={equalityOperatorOptions}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as EqualityOperator })
            }
          />
          <CanvasSimpleSelect
            aria-label="Embed Type"
            tooltip="Type of embed to select or reject upon"
            className="flex-1"
            value={shape.props.embed_name}
            options={embedOperatorOptions}
            onValueChange={(v) =>
              updateShape(shape, { embed_name: v as EmbedOperator })
            }
          />
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
